import React from 'react';
import classNames from 'classnames';
import { NavLink, useHistory } from 'react-router-dom';
import { uuid } from 'uuidv4';
import { gql } from 'apollo-boost';
import { useMutation, useApolloClient } from '@apollo/react-hooks';
import produce from 'immer';

import { PlainButton, Input } from 'components/shared';
import { AnchoredPortal } from 'components/shared/NavigationBar';
import { CollaboratorAvatar } from 'components/shared/CollaboratorAvatar';
import Icon from 'assets/icons';

import { Picker, BaseEmoji, Emoji } from 'emoji-mart';
import { Logger } from 'services';
import { isPeopleGroup, isOrganizationGroup } from 'helpers/groups';
import { useTranslation, useClickOutside } from 'hooks';
import { useFeatureFlagState } from 'context';
import { useGroupsSidebarDispatch } from 'context/GroupsSidebarContext';
import { createGroupMutation, updateGroupMutation } from 'services/apollo/mutations';

import {
  trackingGroupCreateComplete,
  trackingGroupCreateHover,
  trackingGroupCreateClick,
  trackingGroupSelect,
} from 'services/analytics/interceptor/handlers/trackGroup';

import styles from './GroupHeading.module.scss';

interface GroupHeadingProps {
  /** @Group Group data object */
  group?: Group;
  /** @boolean If the group should collapse it's members */
  collapsed?: boolean;
  /** @boolean If the group heading should render with an alternate style */
  alternateStyle?: boolean;
  /** @function Function to collapse/expand group */
  toggleCollapse: () => void;
}

const GroupListQuery = gql`
  query getGroupList {
    me {
      groups {
        id
        name
        emoji
        isAdmin
        groupType
        members {
          ... on Person {
            id
            name
            avatarUrl
          }
        }
      }
    }
  }
`;

export const GroupHeading: React.FC<GroupHeadingProps> = ({
  group,
  collapsed,
  alternateStyle,
  toggleCollapse,
}) => {
  const groupId = group?.id;
  const isEmptyGroup = group === undefined;
  const isOtherPeople = isPeopleGroup(group);
  const isOrganization = isOrganizationGroup(group);

  const canEdit = isEmptyGroup || group?.isAdmin;
  const hasGroupName = group && group.name;

  const { t } = useTranslation();
  const client = useApolloClient();
  const history = useHistory();

  const { groupsFeatureFlag } = useFeatureFlagState();
  const groupsSidebarDispatch = useGroupsSidebarDispatch();

  const [isPending, setIsPending] = React.useState(false);
  const [isEditing, setIsEditing] = React.useState(false);
  const [isEditingEmoji, setIsEditingEmoji] = React.useState(false);

  const [groupName, setGroupName] = React.useState(group?.name || '');
  const [groupEmoji, setGroupEmoji] = React.useState<BaseEmoji>();

  /**
   * When creating a group, the server will return an array of the modified groups
   * This mutation updates the cache and sets the groups to that response data
   */
  const [createGroup] = useMutation(createGroupMutation, {
    update(cache, { data: { createGroup } }) {
      const queryResult: any = cache.readQuery({ query: GroupListQuery });

      if (createGroup.ok) {
        trackingGroupCreateComplete({
          name: groupName,
          type: 'custom',
        });
      } else {
        try {
          cache.writeQuery({
            query: GroupListQuery,
            data: produce(queryResult, (draft: any) => {
              const groups = [...draft.me.groups];
              groups.shift();
              draft.me.groups = groups;
            }),
          });
        } catch (err) {
          Logger.log(err);
        }

        Logger.log('Groups Sidebar - Create Group Failed', createGroup.error);
      }

      setIsPending(false);
    },
  });

  const [updateGroupName] = useMutation(updateGroupMutation);
  const wrapperElement: React.MutableRefObject<HTMLDivElement | null> = React.createRef();

  /**
   * Handles group input name submit
   */
  const handleSubmit = () => {
    setIsEditing(false);
    setIsEditingEmoji(false);

    if (groupId) {
      updateGroupFields();
    } else {
      // If we're creating a new group only allow saving if groupName is not empty
      if (groupName !== '') {
        saveGroup();
      }
    }
  };

  /**
   * Handles click outside when the user has selected the input field
   * Either reverts to the initial name or saves the name change
   * @param e
   * @param groupId
   */
  const handleCancel = () => {
    if (isEditing) {
      const hasEditedName = groupName !== '' && groupName !== group?.name;
      const hasEditedEmoji = groupEmoji && groupEmoji.id !== group?.emoji;

      if (hasEditedName || hasEditedEmoji) {
        handleSubmit();
      } else {
        setGroupEmoji(undefined);
        setGroupName(group?.name || '');
        setIsEditing(false);
        setIsEditingEmoji(false);
      }
    }
  };

  /**
   * Handles group name input changes
   * @param e
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setGroupName(e.target.value);
  };

  /**
   * Called when the heading text is clicked
   * Will enter edit mode if name editing is allowed
   */
  const handleNameClick = () => {
    if (!isPending && canEdit) {
      if (groupsFeatureFlag) {
        setIsEditing(true);
      } else {
        toggleCollapse && toggleCollapse();
      }
    }
  };

  /**
   * Handles the arrow click
   * If it's the new group UI it will enter the edit mode
   * If it's a group it will collapse/expand the members list
   */
  const handleArrowClick = () => {
    if (isEmptyGroup) {
      if (groupsFeatureFlag) {
        setIsEditing(true);
      } else {
        toggleCollapse();
      }
    } else {
      toggleCollapse();
    }
  };

  /**
   * Enters the group heading edit mode
   */
  const handleEdit = () => {
    if (canEdit) {
      if (groupsFeatureFlag) {
        trackingGroupCreateClick();
        setIsEditing(true);
        setIsEditingEmoji(true);
      } else {
        toggleCollapse();
      }
    }
  };

  /**
   * Called when an emoji is selected
   * Closes the emoji pop-over
   * @param emoji
   */
  const handleEmojiSelect = (emoji: BaseEmoji) => {
    setIsEditingEmoji(false);

    if (emoji !== groupEmoji) {
      updateGroupFields();
      setGroupEmoji(emoji);
    }
  };

  /**
   * Called when the group icon/emoji is clicked
   */
  const handleIconClick = () => {
    if ((canEdit && isEditing) || !group) {
      if (groupsFeatureFlag) {
        setIsEditingEmoji((prevState) => !prevState);
      } else {
        toggleCollapse();
      }
    } else if (!isOrganization && !isOtherPeople) {
      history.push(`/group/${groupId}`);
      trackingGroupSelect({
        name: group!.name!,
        type: group!.groupType,
      });
    }
  };

  /**
   * Saves the group name to the server
   */
  const updateGroupFields = () => {
    /** Do not update fields on non-existing group */
    if (!group) {
      return false;
    }

    /** Don't allow saving the group name if it's empty */
    if (groupName === '') {
      handleCancel();
      return false;
    }

    updateGroupName({
      variables: {
        id: groupId,
        name: groupName,
        emoji: groupEmoji?.id || group?.emoji || '',
      },
    })
      .then((res) => {
        Logger.log('Groups Sidebar - Update Group Success', res);
      })
      .catch((err) => {
        Logger.log('Groups Sidebar - Update Group Failed', err);
      });
  };

  /**
   * Saves the new group to the server
   */
  const saveGroup = () => {
    /** Don't allow saving the group if the group name is empty */
    if (groupName === '') {
      handleCancel();
      return false;
    }

    const groupUuid = uuid();
    const queryResult: any = client.readQuery({ query: GroupListQuery });

    const newGroupsList = produce(queryResult, (draft: any) => {
      const groups = [...draft.me.groups];
      groups.unshift({
        id: `g-${groupUuid}`,
        name: groupName,
        emoji: groupEmoji?.id || '',
        isAdmin: true,
        members: [],
        groupType: 'custom',
        __typename: 'Group',
      });
      draft.me.groups = groups;
    });

    setIsPending(true);

    try {
      client.writeQuery({
        query: GroupListQuery,
        data: newGroupsList,
      });

      setGroupName('');
      setGroupEmoji(undefined);

      groupsSidebarDispatch({ type: 'CREATE_GROUP', payload: `g-${groupUuid}` });
      Logger.log('Groups Sidebar - Create Group Optimistically');
    } catch (err) {
      Logger.log(err);
    }

    createGroup({
      variables: {
        name: groupName,
        uuid: groupUuid,
        emoji: groupEmoji?.id || '',
      },
    });
  };

  /**
   * If the local groupName is not empty we use that
   * If there's no groupName we get the group.name from props
   * If there's no group and no groupName input we assume it's the new group ui
   */
  const getGroupName = () => {
    if (groupName !== '') {
      return groupName;
    }

    return !!group ? group?.name : t('groups-sidebar.new-group');
  };

  /**
   * Renders the group heading input field
   */
  const renderEditMode = () => {
    return (
      <Input
        autoFocus
        size="Medium"
        theme="Naked"
        value={groupName}
        onChange={handleChange}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        className={styles.groupHeadingInput}
      />
    );
  };

  /**
   * Renders the group icon
   */
  const renderGroupIcon = () => {
    const emoji = groupEmoji || group?.emoji;

    if (emoji) {
      return (
        <Emoji
          native={false}
          emoji={emoji}
          size={20}
          fallback={() => {
            return (
              <div className={styles.groupIcon}>
                <Icon.Members />
              </div>
            ) as any;
          }}
        />
      );
    }

    if (isOrganization) {
      return (
        <CollaboratorAvatar
          collaborator={{
            displayName: group!.name,
          }}
          type="OrgGroup"
          bordered={false}
          size="SubStandard"
        />
      );
    } else if (isOtherPeople) {
      return (
        <div className={styles.groupIconRound}>
          <Icon.Member />
        </div>
      );
    } else {
      return <Icon.Members />;
    }
  };

  /**
   * Renders the group name after being set
   * Will allow user to rename the group if he's an admin
   */
  const renderGroupName = () => {
    const hasProfileLink = group && !isOrganization && !isOtherPeople;
    const isNewGroup = !group;

    return (
      <>
        {/* Group name cannot be a link if the group type is "organisation" or if it's the "other_people" list */}
        {hasProfileLink && !isPending ? (
          <NavLink
            className={styles.isClickable}
            to={'/group/' + groupId}
            onClick={() => {
              trackingGroupSelect({
                name: group!.name!,
                type: group!.groupType,
              });
            }}
            activeClassName={styles.active}
          >
            <span className={styles.groupHeadingName}>{getGroupName()}</span>
          </NavLink>
        ) : (
          <div
            className={classNames(styles.groupHeadingName, isNewGroup && styles.isClickable)}
            onClick={handleNameClick}
          >
            {getGroupName()}
          </div>
        )}

        {/* Pencil icon that allows for group renaming */}
        {canEdit && (
          <PlainButton
            className={classNames(styles.groupEditButton, !group && styles.isShowing)}
            onClick={handleEdit}
            onMouseEnter={trackingGroupCreateHover}
          >
            <Icon.Edit />
          </PlainButton>
        )}
      </>
    );
  };

  useClickOutside(wrapperElement, handleCancel, 'portals');

  const wrapperClasses = classNames(
    styles.groupHeadingWrapper,
    !collapsed && styles.open,
    !hasGroupName && styles.newGroupWrapper,
    alternateStyle && styles.alternate
  );

  return (
    <div className={wrapperClasses} ref={wrapperElement}>
      <>
        {/* Group Heading Arrow */}
        <PlainButton
          className={classNames(styles.groupTrigger, !collapsed && styles.open)}
          onClick={handleArrowClick}
          onMouseEnter={trackingGroupCreateHover}
        >
          <Icon.TriangleDown
            className={classNames(styles.moreListIcon, !collapsed && styles.open)}
          />
        </PlainButton>

        <div className={styles.navItem}>
          {/* Group Heading Icon / Emoji */}
          <div
            onClick={handleIconClick}
            className={classNames(
              !isOtherPeople && !isOrganization && styles.isClickable,
              styles.groupIcon,
              alternateStyle && styles.alternate
            )}
          >
            {renderGroupIcon()}
          </div>

          {/* Group Heading Name / Input */}
          <div className={styles.groupHeading}>
            {isEditing ? renderEditMode() : renderGroupName()}
          </div>
        </div>
      </>

      {/* Emoji Picker */}
      {isEditingEmoji && (
        <AnchoredPortal>
          <Picker
            title={t('emojiPicker.title')}
            emoji="point_up"
            onSelect={handleEmojiSelect}
            native={false}
            emojiSize={22}
            color="#4583EA"
          />
        </AnchoredPortal>
      )}
    </div>
  );
};

export default GroupHeading;
